'use client';

import ease from '@/constants/ease';
import { AnimatePresence, motion } from 'framer-motion';
import 'keen-slider/keen-slider.min.css';
import Image from 'next/image';
import { FC, useState } from 'react';
import AnimatedHeight from './AnimatedHeight';

type Banner = {
  image: React.ReactNode;
  imageMobile: React.ReactNode;
  backgroundColor: string;
  type: string;
  title: React.ReactNode;
  link: React.ReactNode;
}

export type AiFeaturesProps = {
  backgroundColor: string;
  blurImage: string;
  imageFirst: boolean;
  sectionColorText: string;
  featuresTitleTextColor: string;
  featuresTextColor: string;
  title: React.ReactNode,
  aiFeature: string;
  topFeatures: {
    icon: React.ReactNode,
    title: string;
    description: string;
  }[]
  featuresTitle: string;
  activeFeatureBoxColor: string;
  features: {
    image: React.ReactNode,
    title: string;
    description: string;
  }[]
  companyImage: React.ReactNode,
  companyQuote: string;
  companyQuoteColor: string;
  textColor: string;
  name: string;
  jobTitle: string;
  banner?: Banner;
}

const AiFeatures: FC<AiFeaturesProps> = ({
  backgroundColor,
  blurImage,
  imageFirst,
  sectionColorText,
  featuresTitleTextColor,
  featuresTextColor,
  aiFeature,
  title,
  topFeatures = [],
  featuresTitle,
  activeFeatureBoxColor,
  features = [],
  companyImage,
  companyQuote,
  companyQuoteColor,
  textColor,
  name,
  jobTitle,
  banner,
}) => {
  const [activeFeatureIndex, setFeatureIndex] = useState(0);

  return (
    <div className="bg-violet-900 text-white px-[16px] md:px-[64px] pb-[48px]">
      <div className="flex bg-center">
        <div className="mx-auto max-w-[1384px] overflow-hidden">
          <div style={{ backgroundImage: `url(https://front.com/webassets/rebrand/${blurImage})` }} className={`grid gap-2 md:gap-3 text-left ${backgroundColor} ${sectionColorText} rounded-[16px] bg-center bg-top bg-no-repeat`}>
            <div className="flex justify-left md:justify-center flex-wrap pt-[64px] pb-[48px] md:pb-[120px] px-[16px] md:px-0 overflow-hidden">
              <div className="flex w-full md:justify-center">
                <div className="flex bg-violet-1000 rounded-[16px] md:ml-[32px] items-center">
                  <div className="flex" id="icon">
                    <span className="flex w-[32px] h-[32px] bg-citron-500 rounded-[50%] justify-center items-center mr-[8px] mt-[2px] mb-[2px] ml-[2px]">
                      <Image src="https://front.com/webassets/rebrand/ai-icon.svg" width={37} height={37} alt="ai icon" />
                    </span>
                  </div>
                  <div className="flex text-p2 text-white font-500 mr-[13px]" id="textHeader">
                    {aiFeature}
                  </div>
                </div>
              </div>
              <div className="flex md:justify-center">
                <h2 className={`text-h3 md:text-h1 text-left md:text-center mt-[32px] mb-[64px] ${featuresTitleTextColor}`}>
                  {title}
                </h2>
              </div>
              <div className="flex w-full justify-center flex-wrap md:flex-nowrap">
                {topFeatures.map((feature, index) => (
                  <div key={`top-features-${index}`} className="max-w-[100%] md:max-w-[356px] w-full mr-0 md:mr-[75px] pb-[32px] md:p-0 md:px-0 md:first:ml-[64px]">
                    <div className="flex bg-citron-500 rounded-[50%] w-[32px] h-[32px] justify-center items-center">
                      {feature.icon}
                    </div>
                    <div className={`flex font-500 text-p1 mt-[12px] mb-[16px] ${featuresTitleTextColor}`}>
                      {feature.title}
                    </div>
                    <div className={`flex text-p2 ${featuresTitleTextColor}`}>
                      {feature.description}
                    </div>
                  </div>

                ))}
              </div>

              <div className={`flex w-full flex-wrap md:flex-nowrap justify-center items-start mt-0 md:mt-[112px] ${imageFirst ? "flex-row-reverse" : null}`}>
                <div className={`flex flex-wrap w-full overflow-hidden md:w-[50%] ${imageFirst ? "mr-0 ml-0 px-0 md:mr-[32px] md:ml-[140px]" : "mr-0 ml-0  md:ml-[32px] md:mr-[140px]"}`}>
                  <div className={`${featuresTitleTextColor} text-h1 font-500 mb-[48px] md:ml-[32px] text-balance`}>{featuresTitle}</div>
                  {/* Desktop features list */}
                  <div className="hidden sm:flex flex-wrap">
                    {features.map((feature, index) => (
                      <div key={`ai-feature-${index}`} className={`flex w-full flex-wrap cursor-pointer mt-[24px] rounded-[16px] transition-colors duration-400 ease-in-out-cubic ${activeFeatureIndex === index ? `${activeFeatureBoxColor} ` : null}`}
                        onClick={() => setFeatureIndex(index)}>
                        <div className={`flex ml-[16px] md:ml-[32px] font-500 text-p1 pt-[24px] ${featuresTitleTextColor}`}>
                          {feature.title}
                        </div>
                        <AnimatedHeight
                          isExpanded={index === activeFeatureIndex}
                        >
                          <div className={`flex ${featuresTextColor} text-p2 pl-[32px] pr-[32px] pb-[32px] mt-[24px]`}>
                            {feature.description}
                          </div>
                        </AnimatedHeight>
                      </div>
                    ))}
                  </div>
                  {/* Mobile features list */}
                  <div className="flex sm:hidden flex-wrap">
                    {features.map((feature, index) => (
                      <div key={`ai-feature-${index}`} className={`flex w-full flex-wrap cursor-pointer mt-[24px] ${activeFeatureBoxColor} rounded-[16px]`}>
                        <div className="flex ml-[16px] md:ml-[32px] font-500 text-p1 pt-[24px]">
                          {feature.title}
                        </div>

                        <motion.div
                          className={`flex ${sectionColorText} text-p2 pl-[32px] pr-[32px] pb-[32px] mt-[24px]`}
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{
                            duration: 400 / 1000,
                            ease: ease.appear,
                          }}
                        >
                          {feature.description}
                        </motion.div>

                      </div>
                    ))}
                  </div>
                </div>
                <div className={`hidden relative sm:flex w-full aspect-square md:w-[50%] justify-center mt-[18px] md:mt-0 ${imageFirst ? " ml-0 md:ml-[64px]" : "mr-0 md:mr-[64px]"}`}>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={`ai-feature-${activeFeatureIndex}-${features[activeFeatureIndex].title}`}
                      className="absolute inset-0"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2, ease: 'easeInOut' }}
                    >
                      {features[activeFeatureIndex].image}
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>

              <div className={`flex ${sectionColorText} justify-center flex-wrap pt-[64px] md:pt-[110px]`}>
                {companyImage}
                <div className={`flex text-p2 md:text-h2 font-500 justify-center text-center font-serif md:px-[160px] mt-[30px] mb-[25px] ${companyQuoteColor}`}>
                  {companyQuote}
                </div>
                <div className={`flex w-full flex-wrap md:flex-nowrap justify-center items-center ${textColor} font-500 text-p2`}>
                  {name} <span className="uppercase text-center sm:text-left text-micro1 pl-[14px]">&nbsp;{jobTitle}</span>
                </div>
                {banner ?
                  <div className={`flex flex-wrap md:flex-nowrap items-center ${banner.backgroundColor} ${sectionColorText} rounded-[16px] max-w-[1036px] mx-[16px] mt-[64px] md:mt-[114px]`}>
                    <div className="flex w-full md:w-[20%] hidden md:block">
                      {banner.image}
                    </div>
                    <div className="flex w-full md:w-[20%] block md:hidden">
                      {banner.imageMobile}
                    </div>
                    <div className="flex flex-wrap ml-0 md:ml-[34px] px-[16px] md:px-0 w-full md:w-[60%]">
                      <div className="flex w-full uppercase text-micro1 mt-[24px] md:mt-0">
                        {banner.type}
                      </div>
                      <div className={`flex text-h3 font-500 ${featuresTitleTextColor} text-balance`}>
                        {banner.title}
                      </div>
                    </div>
                    <div className="flex w-full md:w-[20%] px-[16px] md:px-0 my-[24px] md:my-0">
                      {banner.link}
                    </div>
                  </div>
                  : null}

              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AiFeatures;
